import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import apiClient from '@/services/apiClient';

const Codes = ({ list, total }) => (
  <span className="font-mono text-[11px] text-gray-600" title={list.join(', ')}>{list.slice(0, 3).join(', ')}{total > 3 ? ` +${total - 3}` : ''}</span>
);

/** Supplier deliveries whose received bundles are not counted on any PO line yet — with a jump to the screen that counts them. */
export const UnmappedBundles = () => {
  const navigate = useNavigate();
  const [days, setDays] = useState('0');
  const [list, setList] = useState(null);
  useEffect(() => {
    setList(null);
    apiClient.get('/po-followup/unmapped', { params: { min_days: days } }).then((r) => setList(r.data.deliveries)).catch(() => setList([]));
  }, [days]);
  return (
    <div className="space-y-3" data-testid="po-unmapped">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-gray-600 flex-1 min-w-[240px]">Deliveries from suppliers with an open PO, received in the section, with bundles not counted on any PO line.</p>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-[170px]" data-testid="po-unmapped-days"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Any age</SelectItem>
            <SelectItem value="2">Waiting 2+ days</SelectItem>
            <SelectItem value="7">Waiting 7+ days</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {!list ? <Loader2 className="mx-auto my-10 h-6 w-6 animate-spin text-gray-400" />
        : !list.length ? <p className="py-10 text-center text-sm text-gray-500" data-testid="po-unmapped-empty">Every received supplier bundle is counted on a PO.</p> : (
          <div className="overflow-x-auto" data-testid="po-unmapped-table">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Delivery</TableHead><TableHead>Supplier</TableHead><TableHead>Section</TableHead><TableHead>Received</TableHead>
                <TableHead className="text-right">Not on a PO</TableHead><TableHead>Bundles</TableHead><TableHead />
              </TableRow></TableHeader>
              <TableBody>
                {list.map((u) => (
                  <TableRow key={u.entry_id} data-testid={`po-unmapped-row-${u.entry_id}`}>
                    <TableCell className="font-mono text-xs">{u.document_barcode || u.entry_id}<div className="text-[10px] text-gray-400">{u.entry_id}</div></TableCell>
                    <TableCell className="text-sm">{u.vendor_name}</TableCell>
                    <TableCell className="text-xs">{u.section_name}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{u.received_at ? new Date(u.received_at).toLocaleDateString('en-IN') : '—'}
                      <div className={`text-[10px] ${u.days_waiting >= 2 ? 'text-rose-600 font-semibold' : 'text-gray-400'}`}>{u.days_waiting}d waiting</div></TableCell>
                    <TableCell className="text-right font-bold text-amber-700 tabular-nums" data-testid={`po-unmapped-count-${u.entry_id}`}>{u.unassigned_count}<span className="text-xs font-normal text-gray-400"> / {u.received_bundles}</span></TableCell>
                    <TableCell><Codes list={u.unassigned} total={u.unassigned_count} /></TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => navigate(u.path)} data-testid={`po-unmapped-open-${u.entry_id}`}>{u.screen}<ArrowRight className="w-3.5 h-3.5 ml-1" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
    </div>
  );
};
